import React, { useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Download, Upload, Trash2 } from 'lucide-react';
import { useLocalStorage } from './hooks/useLocalStorage';
import { Job, Rep } from './types';
import { getNextFriday, calculateJobDetails } from './utils/helpers';
import { CommissionForm } from './components/CommissionForm';
import { CommissionTable } from './components/CommissionTable';
import { RepModal } from './components/RepModal';
export function App() {
  const [jobs, setJobs] = useLocalStorage<Job[]>('commission-jobs', []);
  const [reps, setReps] = useLocalStorage<Rep[]>('commission-reps', []);
  // State for handling new reps during form submission
  const [pendingJobData, setPendingJobData] = useState<any>(null);
  const [missingRepsQueue, setMissingRepsQueue] = useState<{
    name: string;
    role: string;
  }[]>([]);
  const [currentMissingRep, setCurrentMissingRep] = useState<{
    name: string;
    role: string;
  } | null>(null);
  const [isRepModalOpen, setIsRepModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const handleFormSubmit = (jobData: any) => {
    // Check if closer or setter are new
    const missing: {
      name: string;
      role: string;
    }[] = [];
    // Closer is only required if the job is closed
    if (jobData.isClosed && jobData.closerName) {
      const closerExists = reps.some(r => r.name.toLowerCase() === jobData.closerName.toLowerCase());
      if (!closerExists) missing.push({
        name: jobData.closerName,
        role: 'Closer'
      });
    }
    // Setter is always required now
    if (jobData.setterName) {
      const setterExists = reps.some(r => r.name.toLowerCase() === jobData.setterName.toLowerCase());
      if (!setterExists) missing.push({
        name: jobData.setterName,
        role: 'Setter'
      });
    }
    if (missing.length > 0) {
      setPendingJobData(jobData);
      setMissingRepsQueue(missing);
      setCurrentMissingRep(missing[0]);
      setIsRepModalOpen(true);
    } else {
      processAndSaveJob(jobData, reps);
    }
  };
  // CSV column order for backup export/import
  const CSV_JOB_FIELDS = ['id', 'sitDate', 'closeDate', 'isClosed', 'customerName', 'projectType', 'projectTotal', 'financeCompany', 'financeRate', 'financeFee', 'closerName', 'setterName', 'depositPaid', 'sitPayDate', 'commissionPayDate', 'sitPay', 'netAmount', 'closerRate', 'closerCommission', 'setterRate', 'setterCommission', 'status'] as const;
  const CSV_REP_FIELDS = ['id', 'name', 'commissionRate'] as const;
  const escapeCsvCell = (val: any): string => {
    const str = String(val ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const handleExportData = () => {
    // Jobs sheet
    const jobHeaders = CSV_JOB_FIELDS.join(',');
    const jobRows = jobs.map(job => CSV_JOB_FIELDS.map(field => escapeCsvCell((job as any)[field])).join(','));
    // Reps sheet (appended after a blank line separator)
    const repHeaders = CSV_REP_FIELDS.join(',');
    const repRows = reps.map(rep => CSV_REP_FIELDS.map(field => escapeCsvCell((rep as any)[field])).join(','));
    const csvContent = ['--- JOBS ---', jobHeaders, ...jobRows, '', '--- REPS ---', repHeaders, ...repRows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-backup-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const parseCsvRow = (row: string): string[] => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (inQuotes) {
        if (char === '"' && row[i + 1] === '"') {
          current += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          cells.push(current);
          current = '';
        } else {
          current += char;
        }
      }
    }
    cells.push(current);
    return cells;
  };
  const handleImportData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      try {
        let text = event.target?.result as string;
        // Strip BOM and normalize line endings
        text = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        // Find section markers (flexible matching)
        const jobsStart = lines.findIndex(l => l.toUpperCase().includes('JOBS'));
        const repsStart = lines.findIndex(l => l.toUpperCase().includes('REPS'));
        if (jobsStart === -1 || repsStart === -1) {
          // Try legacy JSON format
          try {
            const data = JSON.parse(text);
            if (data.jobs && Array.isArray(data.jobs)) setJobs(data.jobs);
            if (data.reps && Array.isArray(data.reps)) setReps(data.reps);
            alert('Data imported successfully!');
            return;
          } catch {}
          alert('Could not read this file. Please use a backup file exported from this app.');
          return;
        }
        const numericJobFields = ['projectTotal', 'financeRate', 'financeFee', 'sitPay', 'netAmount', 'closerRate', 'closerCommission', 'setterRate', 'setterCommission'];
        const booleanJobFields = ['isClosed', 'depositPaid'];
        const cleanHeader = (h: string) => h.replace(/['"]/g, '').trim();
        const cleanNumber = (v: string) => parseFloat(v.replace(/[$,"%]/g, '')) || 0;
        // Parse jobs
        const jobHeaderLine = lines[jobsStart + 1];
        const jobHeaders = parseCsvRow(jobHeaderLine).map(cleanHeader);
        const importedJobs: Job[] = [];
        for (let i = jobsStart + 2; i < repsStart; i++) {
          if (lines[i] === '' || lines[i].toUpperCase().includes('---')) continue;
          const values = parseCsvRow(lines[i]);
          const job: any = {};
          jobHeaders.forEach((header, idx) => {
            const val = (values[idx] ?? '').trim();
            if (numericJobFields.includes(header)) {
              job[header] = cleanNumber(val);
            } else if (booleanJobFields.includes(header)) {
              job[header] = val.toLowerCase() === 'true';
            } else {
              job[header] = val;
            }
          });
          if (job.id) importedJobs.push(job as Job);
        }
        // Parse reps
        const repHeaderLine = lines[repsStart + 1];
        const repHeaders = parseCsvRow(repHeaderLine).map(cleanHeader);
        const importedReps: Rep[] = [];
        for (let i = repsStart + 2; i < lines.length; i++) {
          if (lines[i] === '' || lines[i].toUpperCase().includes('---')) continue;
          const values = parseCsvRow(lines[i]);
          const rep: any = {};
          repHeaders.forEach((header, idx) => {
            const val = (values[idx] ?? '').trim();
            if (header === 'commissionRate') {
              rep[header] = cleanNumber(val);
            } else {
              rep[header] = val;
            }
          });
          if (rep.id) importedReps.push(rep as Rep);
        }
        if (importedJobs.length > 0) setJobs(importedJobs);
        if (importedReps.length > 0) setReps(importedReps);
        alert(`Imported ${importedJobs.length} jobs and ${importedReps.length} reps successfully!`);
      } catch {
        alert('Error reading file. Please make sure it is a valid backup CSV.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  const handleRepSaved = (rate: number) => {
    if (!currentMissingRep) return;
    const newRep: Rep = {
      id: crypto.randomUUID(),
      name: currentMissingRep.name,
      commissionRate: rate
    };
    const updatedReps = [...reps, newRep];
    setReps(updatedReps);
    const remainingQueue = missingRepsQueue.slice(1);
    if (remainingQueue.length > 0) {
      setMissingRepsQueue(remainingQueue);
      setCurrentMissingRep(remainingQueue[0]);
    } else {
      // All missing reps added, proceed to save job
      setIsRepModalOpen(false);
      setCurrentMissingRep(null);
      setMissingRepsQueue([]);
      if (pendingJobData) {
        processAndSaveJob(pendingJobData, updatedReps);
        setPendingJobData(null);
      }
    }
  };
  const handleRepCancel = () => {
    setIsRepModalOpen(false);
    setCurrentMissingRep(null);
    setMissingRepsQueue([]);
    setPendingJobData(null);
  };
  const processAndSaveJob = (jobData: any, currentReps: Rep[]) => {
    const closer = jobData.isClosed ? currentReps.find(r => r.name.toLowerCase() === jobData.closerName.toLowerCase()) : null;
    const setter = currentReps.find(r => r.name.toLowerCase() === jobData.setterName.toLowerCase());
    const closerRate = closer?.commissionRate || 0;
    const setterRate = setter?.commissionRate || 0;
    const details = calculateJobDetails(jobData.isClosed, jobData.projectType, jobData.projectTotal, jobData.financeCompany, jobData.financeFee, jobData.depositPaid, closerRate, setterRate, !!setter);
    const jobRecord: Job = {
      id: jobData.editingId || crypto.randomUUID(),
      ...jobData,
      sitPayDate: getNextFriday(jobData.sitDate),
      commissionPayDate: jobData.isClosed ? getNextFriday(jobData.closeDate) : '',
      closerRate,
      setterRate,
      ...details
    };
    delete (jobRecord as any).editingId;
    if (jobData.editingId) {
      // Update existing job
      setJobs(jobs.map(j => j.id === jobData.editingId ? jobRecord : j));
    } else {
      // Add new job
      setJobs([jobRecord, ...jobs]);
    }
    setEditingJob(null);
  };
  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    // Scroll to form
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const handleCancelEdit = () => {
    setEditingJob(null);
  };
  const handleDeleteJob = (id: string) => {
    if (window.confirm('Are you sure you want to delete this job entry?')) {
      setJobs(jobs.filter(j => j.id !== id));
    }
  };
  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear ALL data? This will delete all jobs and reps. This cannot be undone.')) {
      if (window.confirm('Really? Export a backup first if you need the data. Click OK to wipe everything.')) {
        setJobs([]);
        setReps([]);
      }
    }
  };
  const handleMarkDepositPaid = (id: string) => {
    setJobs(jobs.map(job => {
      if (job.id === id) {
        return {
          ...job,
          depositPaid: true,
          status: 'Ready'
        };
      }
      return job;
    }));
  };
  return <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900 pb-20" data-id="element-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-3" data-id="element-3">
            <div className="bg-indigo-600 p-2 rounded-lg" data-id="element-4">
              <Calculator className="w-5 h-5 text-white" data-id="element-5" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight" data-id="element-6">
              Commission Tracker
            </h1>
          </div>
          <div className="flex items-center gap-3" data-id="element-7">
            <span className="text-sm font-medium text-slate-500" data-id="element-8">
              {jobs.length} Job{jobs.length !== 1 ? 's' : ''} Tracked
            </span>
            <div className="flex items-center gap-1" data-id="element-9">
              <button onClick={handleClearAll} title="Clear All Data" className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-10">
                <Trash2 className="w-4 h-4" data-id="element-11" />
              </button>
              <button onClick={handleExportData} title="Export Backup" className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-12">
                <Download className="w-4 h-4" data-id="element-13" />
              </button>
              <button onClick={() => fileInputRef.current?.click()} title="Import Data" className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-14">
                <Upload className="w-4 h-4" data-id="element-15" />
              </button>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv,application/vnd.ms-excel,text/plain" onChange={handleImportData} className="hidden" data-id="element-16" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-17">
        {/* Form Section */}
        <section ref={formRef} data-id="element-18">
          <CommissionForm reps={reps} onSubmit={handleFormSubmit} editingJob={editingJob} onCancelEdit={handleCancelEdit} data-id="element-19" />
        </section>

        {/* Table Section */}
        <section data-id="element-20">
          <div className="mb-4" data-id="element-21">
            <h2 className="text-lg font-semibold text-slate-800" data-id="element-22">
              Job History & Commissions
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-23">
              Sort by Pay Date to see upcoming commission payments. Use Export
              CSV to pull payroll data.
            </p>
          </div>
          <CommissionTable jobs={jobs} onDeleteJob={handleDeleteJob} onMarkDepositPaid={handleMarkDepositPaid} onEditJob={handleEditJob} data-id="element-24" />
        </section>
      </main>

      {/* Modals */}
      <RepModal isOpen={isRepModalOpen} repName={currentMissingRep?.name || ''} role={currentMissingRep?.role || ''} onSave={handleRepSaved} onCancel={handleRepCancel} data-id="element-25" />
    </div>;
}